import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { formatApiError } from "@/lib/auth";
import { Pencil, Trash2, UserPlus, ArrowUp, ArrowDown } from "lucide-react";

const STATUS_TONE = {
  Pending:  "bg-amber-50 text-amber-800 ring-amber-200",
  Approved: "bg-sky-50 text-sky-800 ring-sky-200",
  Active:   "bg-emerald-50 text-emerald-700 ring-emerald-200",
  Rejected: "bg-red-50 text-red-700 ring-red-200",
  Inactive: "bg-slate-100 text-slate-700 ring-slate-200",
};

const EMPTY_FORM = {
  name: "", email: "", phone: "",
  qualification: "", bar_id: "",
  districts: "", languages: "English, Hindi",
  specializations: "Arbitration, Mediation",
  empanelment_status: "Active",
  password: "",
};

const csv = (s) => (s || "").split(",").map(x => x.trim()).filter(Boolean);

export default function AdminNeutrals() {
  const [neutrals, setNeutrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null); // holds the neutral being edited
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/neutrals").then(r => setNeutrals(r.data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setForm(EMPTY_FORM); setAddOpen(true); };
  const openEdit = (n) => {
    setEditing(n);
    setForm({
      name: n.name || "",
      email: n.email || "",
      phone: n.phone || "",
      qualification: n.profile?.qualification || "",
      bar_id: n.profile?.bar_id || "",
      districts: (n.profile?.districts || []).join(", "),
      languages: (n.profile?.languages || []).join(", "),
      specializations: (n.profile?.specializations || []).join(", "),
      empanelment_status: n.profile?.empanelment_status || "Active",
      password: "",
    });
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/neutrals", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        qualification: form.qualification.trim() || null,
        bar_id: form.bar_id.trim() || null,
        districts: csv(form.districts),
        languages: csv(form.languages),
        specializations: csv(form.specializations),
        empanelment_status: form.empanelment_status,
        password: form.password.trim() || null,
      });
      toast.success("Mediator/Arbitrator added.");
      setAddOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    setSaving(true);
    try {
      await api.put(`/neutrals/${editing.id}`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        qualification: form.qualification.trim() || null,
        bar_id: form.bar_id.trim() || null,
        districts: csv(form.districts),
        languages: csv(form.languages),
        specializations: csv(form.specializations),
        empanelment_status: form.empanelment_status,
      });
      toast.success("Profile updated.");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!deleting) return;
    try {
      const r = await api.delete(`/neutrals/${deleting.id}`);
      toast.success(r.data?.message || (r.data?.deactivated ? "Deactivated." : "Removed."));
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= neutrals.length) return;
    const next = [...neutrals];
    [next[idx], next[target]] = [next[target], next[idx]];
    setNeutrals(next); // optimistic
    try {
      await api.post("/neutrals/reorder", { ids: next.map(n => n.id) });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
      load();
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-neutrals">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-court">Mediators / Arbitrators — empanelment</h1>
          <p className="text-sm text-muted-foreground mt-1">Use the up/down arrows on each card to set the order shown on the public panel page.</p>
        </div>
        <Button onClick={openAdd} className="rounded-sm bg-primary" data-testid="add-neutral-btn">
          <UserPlus className="h-4 w-4 mr-2" />Add mediator/arbitrator
        </Button>
      </div>

      {loading ? (
        <div className="text-muted-foreground">Loading…</div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {neutrals.map((n, idx) => (
            <div key={n.id} className="bg-card rounded-sm border border-stone/60 p-6" data-testid={`neutral-card-${n.email}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="shrink-0 w-8 h-8 grid place-items-center rounded-sm bg-muted font-mono text-sm text-court" title="Position on public panel">
                    {idx + 1}
                  </div>
                  <div className="min-w-0">
                    <div className="font-display text-lg text-court truncate">{n.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{n.email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <span className={`inline-flex items-center rounded-sm px-2.5 py-0.5 text-[11px] font-semibold ring-1 ${STATUS_TONE[n.profile?.empanelment_status] || STATUS_TONE.Inactive} mr-1`}>
                    {n.profile?.empanelment_status || "—"}
                  </span>
                  <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-ink disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`move-up-${n.email}`} title="Move up">
                    <ArrowUp className="h-4 w-4" />
                  </button>
                  <button onClick={() => move(idx, 1)} disabled={idx === neutrals.length - 1} className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-ink disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`move-down-${n.email}`} title="Move down">
                    <ArrowDown className="h-4 w-4" />
                  </button>
                  <button onClick={() => openEdit(n)} className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-ink" data-testid={`edit-${n.email}`} title="Edit">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => setDeleting(n)} className="p-1.5 rounded hover:bg-red-50 text-muted-foreground hover:text-red-600" data-testid={`delete-${n.email}`} title="Remove">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="mt-3 text-sm text-muted-foreground">{n.profile?.qualification || "—"}{n.profile?.bar_id ? ` · Bar ${n.profile.bar_id}` : ""}</div>
              <div className="mt-2 text-xs text-muted-foreground">Specialization: {(n.profile?.specializations || []).join(", ") || "—"}</div>
              <div className="mt-1 text-xs text-muted-foreground">Languages: {(n.profile?.languages || []).join(", ") || "—"}</div>
              <div className="mt-1 text-xs text-muted-foreground">Cases handled: {n.profile?.cases_handled ?? 0}</div>
            </div>
          ))}
          {neutrals.length === 0 && (
            <div className="text-muted-foreground col-span-full">No panel members yet. Click &quot;Add mediator/arbitrator&quot; to create the first one.</div>
          )}
        </div>
      )}

      {/* Add dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-[560px]" data-testid="add-neutral-dialog">
          <DialogHeader><DialogTitle>Add mediator/arbitrator</DialogTitle></DialogHeader>
          <form onSubmit={submitAdd}><PanelFields form={form} set={set} isCreate /></form>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={submitAdd} disabled={saving} className="bg-primary" data-testid="add-neutral-submit">
              {saving ? "Saving…" : "Add to panel"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-[560px]" data-testid="edit-neutral-dialog">
          <DialogHeader><DialogTitle>Edit — {editing?.name}</DialogTitle></DialogHeader>
          <form onSubmit={submitEdit}><PanelFields form={form} set={set} isCreate={false} /></form>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={submitEdit} disabled={saving} className="bg-primary" data-testid="edit-neutral-submit">
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="delete-neutral-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {deleting?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes them from the panel. If any cases are still assigned to this person, we&apos;ll mark them Inactive instead of deleting.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="delete-neutral-confirm">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function PanelFields({ form, set, isCreate }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
      <div className="sm:col-span-2">
        <Label>Full name *</Label>
        <Input value={form.name} onChange={e => set("name", e.target.value)} className="mt-1 h-10 rounded-sm" data-testid="np-name" />
      </div>
      <div>
        <Label>Email {isCreate ? "*" : "(read-only)"}</Label>
        <Input value={form.email} onChange={e => set("email", e.target.value)} disabled={!isCreate} className="mt-1 h-10 rounded-sm" data-testid="np-email" />
      </div>
      <div>
        <Label>Phone</Label>
        <Input value={form.phone} onChange={e => set("phone", e.target.value)} className="mt-1 h-10 rounded-sm" data-testid="np-phone" />
      </div>
      <div className="sm:col-span-2">
        <Label>Qualification</Label>
        <Textarea value={form.qualification} onChange={e => set("qualification", e.target.value)} className="mt-1 rounded-sm min-h-[60px]" data-testid="np-qual" placeholder="e.g. LL.M., Queen Mary University of London; LL.B., Delhi University" />
      </div>
      <div>
        <Label>Bar / Registration ID</Label>
        <Input value={form.bar_id} onChange={e => set("bar_id", e.target.value)} className="mt-1 h-10 rounded-sm" data-testid="np-bar" placeholder="D/4650/2017" />
      </div>
      <div>
        <Label>Empanelment status</Label>
        <Select value={form.empanelment_status} onValueChange={(v) => set("empanelment_status", v)}>
          <SelectTrigger className="mt-1 h-10 rounded-sm" data-testid="np-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Pending">Pending</SelectItem>
            <SelectItem value="Approved">Approved</SelectItem>
            <SelectItem value="Active">Active</SelectItem>
            <SelectItem value="Inactive">Inactive</SelectItem>
            <SelectItem value="Rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="sm:col-span-2">
        <Label>Specializations <span className="text-xs text-muted-foreground">(comma-separated)</span></Label>
        <Input value={form.specializations} onChange={e => set("specializations", e.target.value)} className="mt-1 h-10 rounded-sm" data-testid="np-spec" placeholder="Arbitration, Mediation" />
      </div>
      <div className="sm:col-span-2">
        <Label>Languages <span className="text-xs text-muted-foreground">(comma-separated)</span></Label>
        <Input value={form.languages} onChange={e => set("languages", e.target.value)} className="mt-1 h-10 rounded-sm" data-testid="np-lang" placeholder="English, Hindi" />
      </div>
      <div className="sm:col-span-2">
        <Label>Districts / Cities <span className="text-xs text-muted-foreground">(comma-separated)</span></Label>
        <Input value={form.districts} onChange={e => set("districts", e.target.value)} className="mt-1 h-10 rounded-sm" data-testid="np-dist" placeholder="Delhi, Mumbai" />
      </div>
      {isCreate && (
        <div className="sm:col-span-2">
          <Label>Password <span className="text-xs text-muted-foreground">(optional — defaults to Password@123)</span></Label>
          <Input type="text" value={form.password} onChange={e => set("password", e.target.value)} className="mt-1 h-10 rounded-sm" data-testid="np-pass" />
        </div>
      )}
    </div>
  );
}
